import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { examData, getDynamicExamData } from '../data/examData';
import { 
  ClockIcon, 
  DevicePhoneMobileIcon, 
  StarIcon, 
  CheckCircleIcon,
  ChartBarIcon
  // SparklesIcon, LightBulbIcon - 모의고사 기능 비활성화로 인해 임시 제거
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import Header from './common/Header';
import LoginPromptManager from './auth/LoginPromptManager';
import AdminLoginModal from './auth/AdminLoginModal';
import AdminDashboard from './admin/AdminDashboard';
import AutoFeedbackModal from './feedback/AutoFeedbackModal';
import AutoFeedbackTest from './feedback/AutoFeedbackTest';
import GamificationNotifications from './gamification/GamificationNotifications';
import FeedbackImpactTrigger from './feedback/FeedbackImpactTrigger';
// 모의고사 기능 임시 비활성화
// import { getAvailableExamTypes } from '../utils/mockExamGenerator';
import { getKoreanExamName } from '../utils/examNameMapping';

const CBTLanding = () => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState({});
  const [dynamicExamData, setDynamicExamData] = useState(null);
  const [stats, setStats] = useState({
    questions: 0,
    students: 0,
    passRate: 0,
    satisfaction: 0
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(10 * 60 + 30); // 10분 30초 = 630초
  const [diagnosticSearchTerm, setDiagnosticSearchTerm] = useState(''); // 진단평가 검색어
  const [isAnswering, setIsAnswering] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [demoStep, setDemoStep] = useState('question'); // 'question', 'selecting', 'result', 'next'
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showAdminLoginModal, setShowAdminLoginModal] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  
  const { user, isAuthenticated, logout, adminUser, isAdmin, adminLogin, adminLogout, loginWithSocial, loginAsGuest } = useAuth();

  // Demo questions array
  const demoQuestions = [
    {
      question: "옴의 법칙에서 전압이 12V이고 저항이 4Ω일 때 전류는?",
      options: ["① 2A", "② 3A", "③ 4A", "④ 6A"],
      correct: 1,
      subject: "전기기사"
    },
    {
      question: "데이터베이스의 정규화 중 제1정규형의 조건은?",
      options: ["① 원자값만 가짐", "② 부분종속 제거", "③ 이행종속 제거", "④ 다치종속 제거"],
      correct: 0,
      subject: "정보처리기사"
    },
    {
      question: "빵 반죽의 발효 온도로 가장 적합한 것은?",
      options: ["① 20~25℃", "② 26~30℃", "③ 31~35℃", "④ 36~40℃"],
      correct: 1,
      subject: "제빵기능사"
    },
    {
      question: "굴착기 작업 시 안전사항으로 옳은 것은?",
      options: ["① 작업반경 내 출입금지", "② 최대속도 운전", "③ 안전모 미착용", "④ 작업 중 대화"],
      correct: 0,
      subject: "굴착기기능사"
    },
    {
      question: "헤어컷팅 시 가위를 잡는 올바른 방법은?",
      options: ["① 엄지와 약지 사용", "② 엄지와 중지 사용", "③ 검지와 중지 사용", "④ 엄지와 검지 사용"],
      correct: 0,
      subject: "미용기능사"
    }
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Load dynamic exam data from tree structure
  useEffect(() => {
    const loadDynamicData = async () => {
      try {
        // 정적 examData를 우선 설정
        setDynamicExamData(examData);
        
        // 비동기적으로 트리 구조도 시도해보기
        try {
          const dynamicData = await getDynamicExamData();
          
          if (Object.keys(dynamicData).length > 0) {
            setDynamicExamData(dynamicData);
          }
        } catch (dynamicError) {
          // 트리 구조 로딩 실패 시 정적 데이터 유지 (로그 없이)
        }
      } catch (error) {
        // 최후의 수단으로 하드코딩된 데이터 사용
        setDynamicExamData(examData);
      }
    };
    
    loadDynamicData();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    // Only observe specific sections, not all elements with ids
    const sectionsToObserve = document.querySelectorAll('[id^="section-"], [id^="feature-"], .section-element');
    sectionsToObserve.forEach(el => {
      if (el && el.id) {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Animate stats on mount
    const targets = { questions: 2847, students: 15420, passRate: 87.5, satisfaction: 4.8 };
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      setStats({
        questions: Math.floor(targets.questions * easeOutCubic),
        students: Math.floor(targets.students * easeOutCubic),
        passRate: (targets.passRate * easeOutCubic).toFixed(1),
        satisfaction: (targets.satisfaction * easeOutCubic).toFixed(1)
      });

      if (step >= steps) clearInterval(timer);
    }, stepTime);

    return () => clearInterval(timer);
  }, []);

  // Demo automation effect - 실제 수험생처럼 문제 풀기
  useEffect(() => {
    let timeoutId;
    
    const runDemoSequence = () => {
      // 1. 문제 읽는 시간 (0.5초)
      const readingTime = 500;
      
      timeoutId = setTimeout(() => {
        // 2. 답안 선택 (올바른 답 또는 랜덤)
        const currentQ = demoQuestions[currentQuestion];
        const willBeCorrect = Math.random() > 0.3; // 70% 확률로 정답
        const selectedIdx = willBeCorrect ? currentQ.correct : Math.floor(Math.random() * 4);
        
        setSelectedOption(selectedIdx);
        setShowAnswer(true);
        
        // 3. 결과 확인 시간 (1초)
        timeoutId = setTimeout(() => {
          setShowAnswer(false);
          
          // 4. 다음 문제로 이동 (0.3초 후)
          timeoutId = setTimeout(() => {
            setCurrentQuestion(prev => (prev + 1) % demoQuestions.length);
            setSelectedOption(null);
            
            // 다음 사이클 시작
            runDemoSequence();
          }, 300);
        }, 1000);
      }, readingTime);
    };
    
    // 초기 시작
    runDemoSequence();
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // 빈 배열로 한 번만 실행

  // 베타 관련 코드 제거됨

  // 관리자 로그인 핸들러
  const handleAdminLogin = (adminData) => {
    adminLogin(adminData);
    setShowAdminLoginModal(false);
    setShowAdminDashboard(true);
  };

  // 관리자 로그아웃 핸들러
  const handleAdminLogout = () => {
    adminLogout();
    setShowAdminDashboard(false);
  };

  // Timer countdown effect
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          return 10 * 60 + 30; // Reset to 10:30
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, []);

  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const features = [
    {
      icon: <DevicePhoneMobileIcon className="w-8 h-8" />,
      title: "모바일 최적화",
      description: "언제 어디서나 스마트폰으로 편리하게 문제를 풀 수 있는 반응형 디자인입니다.",
      highlight: "언제 어디서나",
      color: "bg-orange-500"
    },
    {
      icon: <ClockIcon className="w-8 h-8" />,
      title: "실시간 타이머",
      description: "실제 시험과 동일한 180분 타이머로 시간 관리 능력을 기를 수 있습니다.",
      highlight: "실전 연습",
      color: "bg-orange-600"
    },
    {
      icon: <ChartBarIcon className="w-8 h-8" />,
      title: "즉시 채점",
      description: "문제를 다 풀면 바로 점수와 정답률을 확인할 수 있어 학습 효과가 뛰어납니다.",
      highlight: "실시간 피드백",
      color: "bg-orange-700"
    },
    {
      icon: <CheckCircleIcon className="w-8 h-8" />,
      title: "진도 관리",  
      description: "답변한 문제와 남은 문제를 한눈에 확인하고 빠르게 이동할 수 있습니다.",
      highlight: "스마트 네비게이션",
      color: "bg-orange-800"
    }
  ];

  // dynamicExamData에서 동적으로 시험 목록 생성
  const currentExamData = dynamicExamData || examData || {};
  
  const examTypes = Object.keys(currentExamData || {}).map(examKey => {
    const exam = currentExamData[examKey];
    
    // 안전성 검사: exam 객체가 유효한지 확인
    if (!exam || typeof exam !== 'object') {
      return null;
    }
    
    // 필수 필드 검증
    if (!exam.title || !exam.subjects) {
      return null;
    }
    
    // Count total available sessions for this exam
    const totalSessions = exam.years ? Object.values(exam.years).reduce((total, year) => {
      return total + Object.keys(year.sessions || {}).length;
    }, 0) : 0;
    
    return {
      key: examKey,
      title: exam.title,
      description: exam.description || '설명 없음',
      questions: `${exam.totalQuestions || 100}문제`,
      timeLimit: `${exam.timeLimit || 180}분`,
      passingScore: `${exam.passingScore || 60}점`,
      popular: exam.popular || false,
      subjects: exam.subjects || [],
      totalSessions: totalSessions
    };
  }).filter(Boolean); // null 값 제거

  // 진단평가용 자격증 검색 필터링
  const filteredDiagnosticExams = examTypes.filter(exam => {
    if (!diagnosticSearchTerm) return true;
    
    const searchTerm = diagnosticSearchTerm.toLowerCase();
    return (
      exam.title.toLowerCase().includes(searchTerm) ||
      exam.description.toLowerCase().includes(searchTerm) ||
      exam.subjects.some(subject => subject.toLowerCase().includes(searchTerm))
    );
  });

  const conveniences = [
    {
      title: "24시간 이용",
      description: "언제든지 접속해서 문제를 풀 수 있는 상시 개방형 시스템",
      icon: "🕐"
    },
    {
      title: "자동 저장",
      description: "답변 내용이 자동으로 저장되어 실수로 나가도 안전",
      icon: "💾"
    },
    {
      title: "빠른 시작",
      description: "회원가입 없이 바로 시작할 수 있는 간편한 접근",
      icon: "⚡"
    },
    {
      title: "상세 해설",
      description: "문제별 정답과 함께 상세한 풀이 과정 제공",
      icon: "📚"
    }
  ];

  const testimonials = [
    {
      name: "김민준",
      role: "전기기사 합격생",
      content: "통근 시간에 스마트폰으로 문제를 풀 수 있어서 정말 효율적이었어요. 실제 시험과 동일한 타이머 덕분에 시간 관리도 완벽했습니다.",
      rating: 5,
      exam: "전기기사 2022년"
    },
    {
      name: "박서연", 
      role: "정보처리기사 준비생",
      content: "회사 점심시간에도 몇 문제씩 풀 수 있어서 꾸준히 학습할 수 있었어요. 진도율이 한눈에 보여서 동기부여가 됩니다.",
      rating: 5,
      exam: "정보처리기사 준비중"
    },
    {
      name: "이동현",
      role: "건축기사 합격생", 
      content: "실제 CBT 환경과 거의 동일해서 시험장에서 당황하지 않고 차분히 풀 수 있었어요. 정말 도움이 많이 됐습니다.",
      rating: 5,
      exam: "건축기사 2023년"
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900 relative overflow-hidden">
      {/* Mouse Follow Effect */}
      <div 
        className="fixed w-32 h-32 rounded-full pointer-events-none z-10 opacity-10 bg-orange-500 blur-xl transition-all duration-300"
        style={{
          left: mousePosition.x - 64,
          top: mousePosition.y - 64,
        }}
      />

      {/* Navigation */}
      <Header 
        showAdminDashboard={showAdminDashboard}
        setShowAdminDashboard={setShowAdminDashboard}
        setShowLoginPrompt={setShowLoginPrompt}
      />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              
              <div className="mb-2">
                <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent animate-pulse">
                  국가공인자격증
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                <span className="text-gray-900">편리한</span>
                <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent"> CBT</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <span className="font-semibold text-orange-600">실제 시험</span> 환경과 동일한 <span className="font-semibold text-orange-600">CBT 시스템</span>으로 국가기술자격 시험을 완벽하게 준비하세요.
              </p>

              {/* Real-time Stats */}
              <div className="grid grid-cols-4 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-orange-600">{stats.questions.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">기출문제</div>
                </div>
                <div className="text-center flex flex-col items-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-orange-600">{stats.students.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">이용자</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-orange-600">{stats.passRate}%</div>
                  <div className="text-sm text-gray-500">합격률</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-orange-600">{stats.satisfaction}</div>
                  <div className="text-sm text-gray-500">만족도</div>
                </div>
              </div>
              
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => navigate('/exams')}
                    className="bg-orange-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
                  >
                    지금 바로 시작하기
                  </button>
                  {/* 모의고사 기능 임시 비활성화 - AI 품질 개선 후 재활성화 예정
                  <button 
                    onClick={() => navigate('/mock-exams')}
                    className="bg-purple-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-purple-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <SparklesIcon className="w-5 h-5" />
                    최신 모의고사
                  </button>
                  */}
                </div>

              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-3xl p-2 sm:p-3 md:p-4 lg:p-6 shadow-xl">
                <div className="bg-white rounded-2xl p-6 sm:p-7 md:p-8 lg:p-10 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-gray-600">CBT 시험 진행중</span>
                    </div>
                    <div className="text-sm text-gray-500 font-mono">{formatTime(timeRemaining)}</div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-900">{demoQuestions[currentQuestion]?.subject}</span>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                        문제 {currentQuestion + 1}
                      </span>
                    </div>
                    
                    <div className="bg-orange-50 p-4 rounded-xl">
                      <p className="text-gray-800 mb-4 text-sm leading-relaxed min-h-[3rem] flex items-center">
                        {demoQuestions[currentQuestion]?.question}
                      </p>
                      <div className="grid grid-cols-2 gap-3 auto-rows-fr">
                        {demoQuestions[currentQuestion]?.options.map((option, index) => {
                          const isSelected = selectedOption === index;
                          const isCorrect = index === demoQuestions[currentQuestion]?.correct;
                          const showCorrectAnswer = showAnswer && isCorrect;
                          const showWrongAnswer = showAnswer && isSelected && !isCorrect;
                          
                          return (
                            <div 
                              key={index}
                              className={`p-3 rounded-lg text-center relative flex items-center justify-center min-h-[3rem] ${
                                showCorrectAnswer ? 'bg-green-100 border-2 border-green-500' :
                                showWrongAnswer ? 'bg-red-100 border-2 border-red-500' :
                                isSelected ? 'bg-orange-100 border-2 border-orange-500' :
                                'bg-gray-50 border border-gray-200'
                              }`}
                            >
                              <span className={`text-sm leading-tight ${
                                showCorrectAnswer ? 'text-green-800 font-medium' :
                                showWrongAnswer ? 'text-red-800 font-medium' :
                                isSelected ? 'text-orange-800 font-medium' :
                                'text-gray-700'
                              }`}>
                                {option}
                              </span>
                              {showCorrectAnswer && (
                                <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                                  정답!
                                </div>
                              )}
                              {showWrongAnswer && (
                                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                                  오답
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div style={{ marginTop: '5px' }}>
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-2 bg-orange-500 rounded-full transition-all duration-500"
                          style={{ width: `${((currentQuestion + 1) / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-orange-600">편의성</span>을 중심으로 한 학습
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              실제 CBT 시험환경과 동일한 인터페이스로 효과적인 학습이 가능합니다
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                id={`feature-${index}`}
                className={`group p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 transform hover:scale-105 ${
                  isVisible[`feature-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className={`w-16 h-16 ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <div className="text-white">
                    {feature.icon}
                  </div>
                </div>
                
                <div className="mb-3">
                  <span className="inline-block bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-medium">
                    {feature.highlight}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Mock Exam Section - 임시 비활성화: AI 모의고사 품질 개선 후 재활성화 예정
      <section className="py-20 px-8 bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <SparklesIcon className="w-4 h-4" />
              AI 예측 기반
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-purple-600">예상모의고사</span>로 실전 대비
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              과거 기출문제 패턴을 분석하여 예상 문제를 생성합니다.<br />
              실제 시험과 유사한 환경에서 실력을 점검해보세요.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {getAvailableExamTypes().slice(0, 6).map((examType) => {
              const koreanName = getKoreanExamName(examType);
              return (
                <div
                  key={examType}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-purple-100 hover:border-purple-300 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg">
                      <LightBulbIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold">
                        예상문제
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                    {koreanName}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    AI가 분석한 출제 패턴으로<br />예상 모의고사를 생성합니다
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      <SparklesIcon className="w-3 h-3 inline mr-1" />
                      패턴 분석 기반
                    </div>
                    <button
                      onClick={() => navigate(`/mock-exam/${examType}`)}
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg transition-all duration-300 group-hover:scale-105"
                    >
                      모의고사 시작
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-10 h-10 rounded-full flex items-center justify-center">
                  <LightBulbIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">예상모의고사 특징</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div className="text-center">
                  <div className="text-purple-600 font-semibold mb-1">패턴 분석</div>
                  <div className="text-gray-600">과거 기출문제의 출제 패턴과 난이도 분포를 AI가 분석</div>
                </div>
                <div className="text-center">
                  <div className="text-purple-600 font-semibold mb-1">신뢰도 표시</div>
                  <div className="text-gray-600">분석 데이터 양에 따른 예측 신뢰도를 투명하게 공개</div>
                </div>
                <div className="text-center">
                  <div className="text-purple-600 font-semibold mb-1">실전 연습</div>
                  <div className="text-gray-600">실제 시험과 동일한 환경에서 시간 제한 모의고사</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Exam Types Section */}
      <section id="exams" className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              다양한 <span className="text-orange-600">국가기술자격</span> 지원
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              전기기사를 비롯한 다양한 국가기술자격 기출문제를 제공합니다
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {examTypes.map((exam, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl border-2 border-orange-500 bg-orange-50 transition-all duration-300 hover:shadow-lg"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{exam.title}</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-500">문제 수</span>
                    <span className="font-semibold">{exam.questions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">시험 시간</span>
                    <span className="font-semibold">{exam.timeLimit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">합격 점수</span>
                    <span className="font-semibold text-orange-600">{exam.passingScore}</span>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="text-sm text-gray-500 mb-2">출제 과목</div>
                  <div className="flex flex-wrap gap-1">
                    {exam.subjects.map((subject, i) => (
                      <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
                
                <Link 
                  to={`/${exam.title}`}
                  className="block w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition-colors font-semibold text-center"
                >
                  {exam.title} 선택
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Convenience Features */}
      <section className="py-20 px-8 bg-orange-500 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              24시간 편의 서비스
            </h2>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              편의점처럼 언제나 열려있는 학습 환경으로 여러분의 합격을 도와드립니다
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {conveniences.map((convenience, index) => (
              <div key={index} className="text-center group">
                <div className="text-6xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {convenience.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{convenience.title}</h3>
                <p className="text-orange-100 leading-relaxed">{convenience.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              합격생 <span className="text-orange-600">후기</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CBT 편의점으로 합격한 분들의 생생한 경험담을 확인해보세요
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-orange-500 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.content}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                  <div className="text-xs text-orange-600 bg-orange-100 px-3 py-1 rounded-full">
                    {testimonial.exam}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            지금 바로 CBT 연습을 시작하세요
          </h2>
          <p className="text-xl text-orange-100 mb-12 max-w-2xl mx-auto leading-relaxed">
            실제 시험과 동일한 환경에서 연습하며 합격 확률을 높여보세요. 
            무료로 모든 기능을 체험할 수 있습니다.
          </p>
          
          <div className="flex justify-center">
            <button 
              onClick={() => navigate('/exams')}
              className="bg-white text-orange-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all duration-300"
            >
              지금 바로 시작하기
            </button>
          </div>
          
          <p className="text-sm text-orange-200 mt-8">
            회원가입 없이 바로 이용 • 100개 기출문제 • 실시간 채점 • 상세 해설
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">C</span>
                </div>
                <span className="text-xl font-bold">CBT 편의점</span>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                편리하고 효율적인 CBT 연습 서비스로 여러분의 합격을 도와드립니다.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">제공 시험</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-orange-400 transition-colors">전기기사</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">정보처리기사</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">건축기사</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">기타 기사급</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">학습 도구</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-orange-400 transition-colors">기출문제</a></li>
                {/* 모의고사 기능 임시 비활성화 */}
                {/* <li><a href="#" className="hover:text-orange-400 transition-colors">모의고사</a></li> */}
                <li><a href="#" className="hover:text-orange-400 transition-colors">오답노트</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">학습통계</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">고객지원</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/about" className="hover:text-orange-400 transition-colors">서비스 소개</a></li>
                <li><a href="/contact" className="hover:text-orange-400 transition-colors">문의하기</a></li>
                <li><a href="/terms" className="hover:text-orange-400 transition-colors">이용약관</a></li>
                <li><a href="/privacy" className="hover:text-orange-400 transition-colors">개인정보처리방침</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2024 CBT 편의점. 언제 어디서나 편리한 CBT 연습 서비스.
            </p>
          </div>
        </div>
      </footer>

      {/* Login Prompt */}
      {showLoginPrompt && (
        <LoginPromptManager
          userProgress={{
            solvedQuestions: 0,
            wrongAnswers: 0,
            sessionMinutes: 0
          }}
          isLoggedIn={isAuthenticated}
          onLogin={async (provider) => {
            try {
              if (provider === 'guest') {
                loginAsGuest();
                setShowLoginPrompt(false);
              } else {
                await loginWithSocial(provider);
                setShowLoginPrompt(false);
              }
            } catch (error) {
              console.error('Login failed:', error);
            }
          }}
          onClose={() => setShowLoginPrompt(false)}
          forceShow={showLoginPrompt}
          isDiagnosticMode={false}
        />
      )}

      {/* Admin Login Modal */}
      <AdminLoginModal 
        isOpen={showAdminLoginModal} 
        onClose={() => setShowAdminLoginModal(false)} 
        onAdminLogin={handleAdminLogin}
      />

      {/* Admin Dashboard */}
      <AdminDashboard 
        isOpen={showAdminDashboard} 
        onClose={() => setShowAdminDashboard(false)} 
      />

      {/* Auto Feedback Modal */}
      <AutoFeedbackModal />

      {/* Auto Feedback Test (Development Only) */}
      {process.env.NODE_ENV === 'development' && <AutoFeedbackTest />}

      {/* Gamification Components */}
      <GamificationNotifications />
    </div>
  );
};

export default CBTLanding;